import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, LogOut, Mail, MailOpen, Paperclip, Trash2 } from "lucide-react";
import { api, errorText } from "@/lib/api";
import { Logo } from "@/components/site/Logo";

const input = "w-full bg-transparent border-b border-black/20 focus:border-[var(--gold)] focus:outline-none py-3 text-[var(--ink)] placeholder:text-stone-500";

const Login = ({ onDone }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try { const { data } = await api.post("/auth/login", { email, password }); onDone(data); }
    catch (x) { setErr(errorText(x)); }
    finally { setBusy(false); }
  };
  return (
    <div className="min-h-screen flex items-center justify-center px-5">
      <form onSubmit={submit} data-testid="admin-login-form" className="w-full max-w-sm space-y-6">
        <Logo className="w-14 h-14 text-[var(--ink)]" />
        <h1 className="font-display text-4xl">Private <em className="text-[var(--gold)]">messages</em></h1>
        <input data-testid="admin-email-input" className={input} type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} required />
        <input data-testid="admin-password-input" className={input} type="password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} required />
        {err && <p data-testid="admin-login-error" className="text-sm text-red-400">{err}</p>}
        <button data-testid="admin-login-btn" disabled={busy} className="w-full rounded-full bg-[var(--ink)] text-white py-4 text-[12px] uppercase tracking-[0.22em] hover:bg-[var(--gold)] transition-colors disabled:opacity-60 inline-flex justify-center gap-2">
          {busy && <Loader2 size={16} className="animate-spin" />} Sign in
        </button>
      </form>
    </div>
  );
};

const MessageRow = ({ m, onToggle, onDelete }) => (
  <article data-testid={`admin-message-${m.id}`} className={`border-b border-black/10 py-6 grid md:grid-cols-12 gap-4 ${m.read ? "opacity-60" : ""}`}>
    <div className="md:col-span-3">
      <p className="font-display text-xl">{m.name}</p>
      <a href={`mailto:${m.email}`} className="text-sm text-[var(--gold)] break-all">{m.email}</a>
      {m.organization && <p className="text-sm text-stone-500">{m.organization}</p>}
    </div>
    <div className="md:col-span-7">
      <p className="text-[11px] uppercase tracking-[0.2em] text-stone-500 mb-2">{m.inquiry_type} · {new Date(m.created_at).toLocaleString()}</p>
      <p className="text-stone-800 whitespace-pre-wrap leading-relaxed">{m.message}</p>
      {m.attachment?.id && (
        <a data-testid={`admin-attachment-${m.id}`} href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/attachments/${m.attachment.id}`}
          className="mt-3 inline-flex items-center gap-2 border border-[var(--gold)]/60 px-3 py-2 text-sm text-stone-800 hover:bg-[var(--gold)] hover:text-white transition-colors">
          <Paperclip size={14} /> {m.attachment.filename}
        </a>
      )}
    </div>
    <div className="md:col-span-2 flex md:justify-end gap-2">
      <button data-testid={`admin-toggle-read-${m.id}`} onClick={() => onToggle(m)} className="p-2 border border-black/15 hover:border-[var(--gold)]" title={m.read ? "Mark unread" : "Mark read"}>
        {m.read ? <Mail size={16} /> : <MailOpen size={16} />}
      </button>
      <button data-testid={`admin-delete-${m.id}`} onClick={() => onDelete(m)} className="p-2 border border-black/15 hover:border-red-400 hover:text-red-400" title="Delete">
        <Trash2 size={16} />
      </button>
    </div>
  </article>
);

export default function Admin() {
  const [user, setUser] = useState(null);
  const [msgs, setMsgs] = useState([]);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/admin/messages"); setMsgs(Array.isArray(data) ? data : []); }
    catch (e) { toast.error(errorText(e)); }
  }, []);

  useEffect(() => {
    api.get("/auth/me").then((r) => setUser(r.data)).catch(() => setUser(false));
  }, []);

  useEffect(() => {
    if (user) load();
  }, [user, load]);

  const toggle = async (m) => {
    const { data } = await api.patch(`/admin/messages/${m.id}`, { read: !m.read });
    setMsgs((xs) => xs.map((x) => (x.id === m.id ? data : x)));
  };
  const remove = async (m) => {
    await api.delete(`/admin/messages/${m.id}`);
    setMsgs((xs) => xs.filter((x) => x.id !== m.id));
    toast.success("Message deleted");
  };
  const logout = async () => { await api.post("/auth/logout"); setUser(false); };

  if (user === null) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin" /></div>;
  if (!user) return <Login onDone={setUser} />;

  const unread = msgs.filter((m) => !m.read).length;
  return (
    <div data-testid="admin-portal" className="max-w-6xl mx-auto px-5 sm:px-10 py-12">
      <header className="flex items-center justify-between mb-12">
        <div className="flex items-center gap-4">
          <Logo className="w-12 h-12 text-[var(--ink)]" />
          <div>
            <h1 className="font-display text-3xl">Messages</h1>
            <p data-testid="admin-unread-count" className="text-sm text-stone-500">{msgs.length} total · {unread} unread</p>
          </div>
        </div>
        <button data-testid="admin-logout-btn" onClick={logout} className="inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.2em] text-stone-600 hover:text-black">
          <LogOut size={14} /> Sign out
        </button>
      </header>
      {msgs.length === 0 ? (
        <p data-testid="admin-empty" className="text-stone-500 py-20 text-center font-display text-2xl italic">No messages yet.</p>
      ) : msgs.map((m) => <MessageRow key={m.id} m={m} onToggle={toggle} onDelete={remove} />)}
    </div>
  );
}
